import { toast } from '@heroui/react';
import { useState, useMemo } from 'react';

import type { SortingState, PaginationState } from '@hs/ui';
import { AlertDialog, Button, Chip, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { AiProviderEditorModal } from './AiProviderEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type AiProvider = {
  id: string;
  code?: string | null;
  name?: string | null;
  hasApiKey?: boolean | null;
  hasAccessKeyId?: boolean | null;
  hasSecretAccessKey?: boolean | null;
  baseUrl?: string | null;
  timeout?: number | null;
  logoUrl?: string | null;
  website?: string | null;
  isEnabled?: boolean | null;
  createdAt: string;
};

export function AiProvidersManagement() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AiProvider | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<AiProvider | null>(null);
  const [deletingProviderId, setDeletingProviderId] = useState<string | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];

  const { data, isLoading, refetch } = trpc.aiProvider.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortItem
        ? { sortBy: sortItem.id, sortOrder: sortItem.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const providers = (data?.items ?? []) as AiProvider[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteProviderMutation = trpc.aiProvider.delete.useMutation({
    onSuccess: () => {
      toast.success('AI 供应商删除成功');
      setDeletingProviderId(null);
      setDeleteModalOpen(false);
      setProviderToDelete(null);
      void utils.aiProvider.list.invalidate();
    },
    onSettled: () => {
      setDeletingProviderId(null);
    },
  });

  const handleCreate = () => {
    setEditingProvider(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (provider: AiProvider) => {
    setEditingProvider(provider);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingProvider(null);
    void refetch();
  };

  const handleDelete = (provider: AiProvider) => {
    setProviderToDelete(provider);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!providerToDelete) return;
    setDeletingProviderId(providerToDelete.id);
    try {
      await deleteProviderMutation.mutateAsync({ id: providerToDelete.id });
    } catch {
      /* onError */
    }
  };

  const columnOverrides = useMemo(
    () => ({
      isEnabled: {
        cell: (row: AiProvider) => (
          <Chip color={row.isEnabled ? 'success' : 'default'} size="sm" variant="soft">
            <Chip.Label>{row.isEnabled ? '已启用' : '已停用'}</Chip.Label>
          </Chip>
        ),
      },
      baseUrl: {
        cell: (row: AiProvider) => row.baseUrl || '—',
      },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<AiProvider>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="ai_provider"
          data={providers}
          recordCount={total}
          pageCount={pageCount}
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          columnOverrides={columnOverrides}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Button onPress={handleCreate}>
                <Plus className="size-4" stroke={1.5} />
                创建
              </Button>
              <Button variant="secondary" onPress={() => void refetch()}>
                <RefreshCw className="size-4" stroke={1.5} />
                刷新
              </Button>
            </div>
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<AiProvider>>
            {({ row }) => {
              const provider = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => handleEdit(provider)}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(provider)}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <AiProviderEditorModal
        open={editorModalOpen}
        provider={editingProvider}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingProvider(null);
        }}
        onSuccess={handleEditorSuccess}
      />

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除 AI 供应商「{providerToDelete?.name ?? providerToDelete?.code}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setProviderToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingProviderId === providerToDelete?.id}
                  onPress={() => void confirmDelete()}
                >
                  {deletingProviderId === providerToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
